<script setup lang="ts">
import { watch, onMounted, onUnmounted } from 'vue'
import { AlertTriangle, Loader2 } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  show: boolean
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmColor?: 'red' | 'primary'
  loading?: boolean
  icon?: boolean
}>(), {
  title: 'Confirm Action',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmColor: 'red',
  loading: false,
  icon: true
})

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show && !props.loading) {
    emit('cancel')
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

watch(() => props.show, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-dialog">
      <div v-if="show" class="modal-overlay backdrop-blur-sm" @click.self="!loading && emit('cancel')">
        <div class="modal-content max-w-md" @click.stop>
          <div class="p-6">
            <div class="flex items-start gap-4">
              <div
                v-if="icon"
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                :class="confirmColor === 'red' ? 'bg-danger-500/10' : 'bg-primary-500/10'"
              >
                <AlertTriangle
                  class="w-5 h-5"
                  :class="confirmColor === 'red' ? 'text-danger-400' : 'text-primary-400'"
                />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-lg font-semibold text-surface-100">{{ title }}</h3>
                <p class="mt-2 text-sm text-surface-400">{{ message }}</p>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3 px-6 pb-6">
            <button
              class="btn-secondary"
              :disabled="loading"
              @click="emit('cancel')"
            >
              {{ cancelText }}
            </button>
            <button
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="confirmColor === 'red'
                ? 'bg-danger-600 hover:bg-danger-700 text-white'
                : 'bg-primary-600 hover:bg-primary-700 text-white'"
              :disabled="loading"
              @click="emit('confirm')"
            >
              <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.confirm-dialog-enter-active,
.confirm-dialog-leave-active {
  transition: opacity 0.15s ease;
}
.confirm-dialog-enter-from,
.confirm-dialog-leave-to {
  opacity: 0;
}
</style>
